/**
 * IP Geolocation Service
 * Centralized geolocation detection through edge function
 */

import { supabase } from '@/integrations/supabase/client';

interface GeolocationData {
  country: string;
  timezone: string;
  currency: string;
  locale: string;
}

export interface FullGeolocationData {
  country_code: string;
  country_name: string;
  city: string | null;
  region: string | null;
  region_code: string | null;
  postal: string | null;
  latitude: number | null;
  longitude: number | null;
  timezone: string;
  currency: string;
  locale: string;
}

// locale-data: country-to-locale-fallback - Frontend fallback mapping when edge function unavailable
// This is NOT a locale configuration - it's a country→locale mapping for geolocation defaults.
// The locale values here are suggestions based on country; actual locale availability comes from system_locale_configs.
const COUNTRY_TO_LOCALE_MAP: Record<string, GeolocationData> = {
  US: { country: 'US', locale: 'en-US', currency: 'USD', timezone: 'America/New_York' },
  GB: { country: 'GB', locale: 'en-GB', currency: 'GBP', timezone: 'Europe/London' },
  CA: { country: 'CA', locale: 'en-CA', currency: 'CAD', timezone: 'America/Toronto' },
  AU: { country: 'AU', locale: 'en-AU', currency: 'AUD', timezone: 'Australia/Sydney' },
  MX: { country: 'MX', locale: 'es-MX', currency: 'MXN', timezone: 'America/Mexico_City' },
  ES: { country: 'ES', locale: 'es-ES', currency: 'EUR', timezone: 'Europe/Madrid' },
  AR: { country: 'AR', locale: 'es-AR', currency: 'ARS', timezone: 'America/Argentina/Buenos_Aires' },
  CO: { country: 'CO', locale: 'es-CO', currency: 'COP', timezone: 'America/Bogota' },
  CL: { country: 'CL', locale: 'es-CL', currency: 'CLP', timezone: 'America/Santiago' },
  BR: { country: 'BR', locale: 'pt-BR', currency: 'BRL', timezone: 'America/Sao_Paulo' },
  PT: { country: 'PT', locale: 'pt-PT', currency: 'EUR', timezone: 'Europe/Lisbon' },
  FR: { country: 'FR', locale: 'fr-FR', currency: 'EUR', timezone: 'Europe/Paris' },
  BE: { country: 'BE', locale: 'fr-BE', currency: 'EUR', timezone: 'Europe/Brussels' },
  DE: { country: 'DE', locale: 'de-DE', currency: 'EUR', timezone: 'Europe/Berlin' },
  AT: { country: 'AT', locale: 'de-AT', currency: 'EUR', timezone: 'Europe/Vienna' },
  CH: { country: 'CH', locale: 'de-CH', currency: 'CHF', timezone: 'Europe/Zurich' },
  IT: { country: 'IT', locale: 'it-IT', currency: 'EUR', timezone: 'Europe/Rome' },
  CN: { country: 'CN', locale: 'zh-CN', currency: 'CNY', timezone: 'Asia/Shanghai' },
  TW: { country: 'TW', locale: 'zh-TW', currency: 'TWD', timezone: 'Asia/Taipei' },
  HK: { country: 'HK', locale: 'zh-HK', currency: 'HKD', timezone: 'Asia/Hong_Kong' },
  JP: { country: 'JP', locale: 'ja-JP', currency: 'JPY', timezone: 'Asia/Tokyo' },
  KR: { country: 'KR', locale: 'ko-KR', currency: 'KRW', timezone: 'Asia/Seoul' },
  SA: { country: 'SA', locale: 'ar-SA', currency: 'SAR', timezone: 'Asia/Riyadh' },
  AE: { country: 'AE', locale: 'ar-AE', currency: 'AED', timezone: 'Asia/Dubai' },
  EG: { country: 'EG', locale: 'ar-EG', currency: 'EGP', timezone: 'Africa/Cairo' },
  RU: { country: 'RU', locale: 'ru-RU', currency: 'RUB', timezone: 'Europe/Moscow' },
  IN: { country: 'IN', locale: 'hi-IN', currency: 'INR', timezone: 'Asia/Kolkata' },
};

// Persistent cache key for geolocation data (localStorage with 24h TTL)
const GEO_CACHE_KEY = 'ip_geolocation_cache';
const GEO_FULL_CACHE_KEY = 'ip_geolocation_full_cache';
const GEO_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedGeoData<T> {
  data: T;
  timestamp: number;
}

/**
 * Get cached geolocation data from localStorage with TTL validation
 * Uses localStorage (persists across sessions) instead of sessionStorage
 * to reduce API calls and stay under rate limits
 */
function getCachedGeoData<T>(cacheKey: string): T | null {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const parsed: CachedGeoData<T> = JSON.parse(cached);
      // Check if cache is still valid (24 hours)
      if (Date.now() - parsed.timestamp < GEO_CACHE_TTL_MS) {
        return parsed.data;
      }
      // Expired - remove it
      localStorage.removeItem(cacheKey);
    }
  } catch {
    // Corrupted cache - clean up
    try {
      localStorage.removeItem(cacheKey);
    } catch {
      // Silent fail
    }
  }
  return null;
}

/**
 * Cache geolocation data in localStorage with timestamp for TTL
 */
function setCachedGeoData<T>(cacheKey: string, data: T): void {
  try {
    const cached: CachedGeoData<T> = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(cached));
  } catch {
    // Silent fail - storage might be full
  }
}

/**
 * Detect user's country and locale settings based on IP address
 * Uses centralized edge function for all IP geolocation
 * Results are cached in sessionStorage to prevent duplicate calls
 */
export async function detectLocaleByIP(): Promise<GeolocationData | null> {
  // Check session cache first
  const cached = getCachedGeoData<GeolocationData>(GEO_CACHE_KEY);
  if (cached) {
    return cached;
  }

  try {
    const { data, error } = await supabase.functions.invoke('ip-geolocation');
    
    if (error || !data) {
      return null;
    }

    const countryCode = data.country_code;
    
    if (!countryCode) {
      return null;
    }
    
    // Return mapped locale data or use data from edge function
    const result = COUNTRY_TO_LOCALE_MAP[countryCode] || {
      country: countryCode,
      locale: data.locale || 'en-US',
      currency: data.currency || 'USD',
      timezone: data.timezone || 'UTC',
    };
    
    // Cache for this session
    setCachedGeoData(GEO_CACHE_KEY, result);
    
    return result;
  } catch {
    return null;
  }
}

/**
 * Detect full geolocation data including city, region, and coordinates
 * Used for prefilling registration forms
 * Results are cached in sessionStorage to prevent duplicate calls
 */
export async function detectFullGeolocation(): Promise<FullGeolocationData | null> {
  // Check session cache first
  const cached = getCachedGeoData<FullGeolocationData>(GEO_FULL_CACHE_KEY);
  if (cached) {
    return cached;
  }

  try {
    const { data, error } = await supabase.functions.invoke('ip-geolocation');
    
    if (error || !data) {
      return null;
    }

    const countryCode = data.country_code;
    
    if (!countryCode) {
      return null;
    }
    
    const result: FullGeolocationData = {
      country_code: countryCode,
      country_name: data.country_name || '',
      city: data.city || null,
      region: data.region || null,
      region_code: data.region_code || null,
      postal: data.postal || null,
      latitude: data.latitude || null,
      longitude: data.longitude || null,
      timezone: data.timezone || 'UTC',
      currency: data.currency || 'USD',
      locale: data.locale || 'en-US',
    };
    
    // Cache for this session
    setCachedGeoData(GEO_FULL_CACHE_KEY, result);
    
    return result;
  } catch {
    return null;
  }
}

/**
 * Get timezone from browser (fallback method)
 */
export function getTimezoneFromBrowser(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

// locale-data: browser-currency-fallback - Currency derivation from browser locale
// Used when IP geolocation is not called (anonymous browsing)
const BROWSER_LOCALE_CURRENCY_MAP: Record<string, string> = {
  'en-US': 'USD', 'en-GB': 'GBP', 'en-CA': 'CAD', 'en-AU': 'AUD', 'en-NZ': 'NZD',
  'es-MX': 'MXN', 'es-ES': 'EUR', 'es-AR': 'ARS', 'es-CO': 'COP', 'es-CL': 'CLP',
  'pt-BR': 'BRL', 'pt-PT': 'EUR',
  'de-DE': 'EUR', 'de-AT': 'EUR', 'de-CH': 'CHF',
  'fr-FR': 'EUR', 'fr-CA': 'CAD', 'fr-BE': 'EUR', 'fr-CH': 'CHF',
  'it-IT': 'EUR', 'it-CH': 'CHF',
  'nl-NL': 'EUR', 'nl-BE': 'EUR',
  'ja-JP': 'JPY', 'zh-CN': 'CNY', 'zh-TW': 'TWD', 'zh-HK': 'HKD', 'ko-KR': 'KRW',
  'ar-SA': 'SAR', 'ar-AE': 'AED', 'ar-EG': 'EGP',
  'ru-RU': 'RUB', 'hi-IN': 'INR', 'pl-PL': 'PLN', 'tr-TR': 'TRY',
  'sv-SE': 'SEK', 'da-DK': 'DKK', 'nb-NO': 'NOK', 'fi-FI': 'EUR',
};

/**
 * Derive currency from browser locale (navigator.language)
 * Falls back to USD if locale is not in the map
 */
export function deriveCurrencyFromBrowserLocale(browserLocale?: string): string {
  if (!browserLocale) return 'USD';
  
  // Try exact match first (e.g., "en-GB")
  if (BROWSER_LOCALE_CURRENCY_MAP[browserLocale]) {
    return BROWSER_LOCALE_CURRENCY_MAP[browserLocale];
  }
  
  // Try with uppercase region (e.g., "en-gb" -> "en-GB")
  const normalized = browserLocale.includes('-')
    ? `${browserLocale.split('-')[0]}-${browserLocale.split('-')[1].toUpperCase()}`
    : browserLocale;
  
  if (BROWSER_LOCALE_CURRENCY_MAP[normalized]) {
    return BROWSER_LOCALE_CURRENCY_MAP[normalized];
  }
  
  // Default fallback
  return 'USD';
}
